import React from 'react';

/* ─── inline styles ────────────────────────────────────────────────────────── */
const S = {
  page: {
    background: '#0e1117',
    minHeight: '100vh',
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: '#e8e0d0',
    overflowX: 'hidden',
    textAlign: 'left',
  },

  /* ── Hero ── */
  hero: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem 1.5rem',
    textAlign: 'center',
    background: 'radial-gradient(ellipse at 50% 40%, #1a2340 0%, #0e1117 70%)',
    overflow: 'hidden',
  },
  heroGrain: {
    position: 'absolute', inset: 0,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
    opacity: 0.5,
    pointerEvents: 'none',
  },
  heroEyecatch: {
    position: 'relative',
    marginBottom: '2.5rem',
    animation: 'floatBob 4s ease-in-out infinite',
  },
  heroTagline: {
    fontSize: 'clamp(0.75rem, 2vw, 0.9rem)',
    letterSpacing: '0.35em',
    textTransform: 'uppercase',
    color: '#c8a84b',
    marginBottom: '1rem',
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.2s forwards',
  },
  heroTitle: {
    fontSize: 'clamp(2.2rem, 7vw, 5rem)',
    fontWeight: 900,
    lineHeight: 1.05,
    margin: 0,
    background: 'linear-gradient(135deg, #f0d060 0%, #c8853a 50%, #e8b84b 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    opacity: 0,
    animation: 'fadeUp 0.9s ease 0.4s forwards',
  },
  heroSub: {
    marginTop: '1.5rem',
    maxWidth: '560px',
    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
    color: '#a09080',
    lineHeight: 1.7,
    opacity: 0,
    animation: 'fadeUp 0.9s ease 0.6s forwards',
  },
  heroDivider: {
    marginTop: '3rem',
    width: '60px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #c8a84b, transparent)',
    opacity: 0,
    animation: 'fadeUp 0.9s ease 0.8s forwards',
  },
  scrollHint: {
    position: 'absolute',
    bottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.4rem',
    color: '#504840',
    fontSize: '0.75rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    opacity: 0,
    animation: 'fadeUp 1s ease 1.2s forwards',
  },
  scrollArrow: {
    width: '20px',
    height: '20px',
    borderRight: '2px solid #504840',
    borderBottom: '2px solid #504840',
    transform: 'rotate(45deg)',
    animation: 'scrollBounce 1.5s ease-in-out infinite',
  },

  /* ── Rules page ── */
  rulesPage: {
    maxWidth: '860px',
    margin: '0 auto',
    padding: '5rem 2rem 6rem',
  },
  sectionWrap: {
    marginBottom: '5rem',
    opacity: 0,
    animation: 'fadeUp 0.8s ease forwards',
  },
  sectionLabel: {
    fontSize: '0.72rem',
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: '#c8a84b',
    marginBottom: '0.6rem',
  },
  sectionTitle: {
    fontSize: 'clamp(1.4rem, 4vw, 2rem)',
    fontWeight: 700,
    color: '#f0e8d8',
    margin: '0 0 0.5rem',
    borderBottom: '1px solid rgba(200,168,75,0.2)',
    paddingBottom: '0.75rem',
  },
  sectionIntro: {
    color: '#9a8878',
    lineHeight: 1.8,
    fontSize: '0.95rem',
    marginBottom: '2rem',
    fontStyle: 'italic',
  },
  rulesList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  ruleItem: {
    display: 'grid',
    gridTemplateColumns: '2.5rem 1fr',
    gap: '1rem',
    alignItems: 'start',
    padding: '1.1rem 1.25rem',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(200,168,75,0.12)',
    borderRadius: '8px',
    transition: 'border-color 0.2s, background 0.2s',
  },
  ruleNum: {
    fontFamily: "'Courier New', monospace",
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#c8a84b',
    lineHeight: 1.4,
    userSelect: 'none',
  },
  ruleBody: {
    lineHeight: 1.7,
    fontSize: '0.95rem',
  },
  ruleTitle: {
    fontWeight: 700,
    color: '#f0e8d8',
    display: 'block',
    marginBottom: '0.2rem',
    fontSize: '1rem',
  },
  ruleText: {
    color: '#8a7a6a',
    fontSize: '0.88rem',
  },

  /* ── Footer rule strip ── */
  footerStrip: {
    borderTop: '1px solid rgba(200,168,75,0.12)',
    padding: '2rem',
    textAlign: 'center',
    color: '#40382e',
    fontSize: '0.78rem',
    letterSpacing: '0.1em',
  },
};

/* ─── SVG Hero Graphic ──────────────────────────────────────────────────────── */
function HeroGraphic() {
  return (
    <svg width="220" height="220" viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer glow ring */}
      <circle cx="110" cy="110" r="100" stroke="url(#ringGrad)" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.5"/>
      <circle cx="110" cy="110" r="84" stroke="rgba(200,168,75,0.15)" strokeWidth="1"/>

      {/* Pokéball body */}
      <circle cx="110" cy="110" r="62" fill="rgba(20,26,40,0.9)" stroke="url(#ballGrad)" strokeWidth="2.5"/>

      {/* Top half red → gold */}
      <path d="M48 110 A62 62 0 0 1 172 110 Z" fill="url(#topGrad)" opacity="0.85"/>

      {/* Centre band */}
      <rect x="48" y="106" width="124" height="8" fill="#0e1117"/>
      <rect x="48" y="107.5" width="124" height="1" fill="rgba(200,168,75,0.5)"/>
      <rect x="48" y="111.5" width="124" height="1" fill="rgba(200,168,75,0.5)"/>

      {/* Centre button ring */}
      <circle cx="110" cy="110" r="14" fill="#0e1117" stroke="url(#btnGrad)" strokeWidth="2.5"/>
      <circle cx="110" cy="110" r="8" fill="url(#innerBtn)"/>
      <circle cx="106" cy="106" r="2" fill="rgba(255,255,255,0.3)"/>

      {/* Decorative sparks */}
      {[0,60,120,180,240,300].map((deg, i) => {
        const r = 96;
        const x = 110 + r * Math.cos((deg * Math.PI) / 180);
        const y = 110 + r * Math.sin((deg * Math.PI) / 180);
        return <circle key={i} cx={x} cy={y} r="2" fill="#c8a84b" opacity={0.4 + (i % 3) * 0.2}/>;
      })}

      {/* Lightning bolts */}
      <path d="M140 58 L133 74 L139 74 L130 92 L145 72 L138 72 Z" fill="#f0d060" opacity="0.7"/>
      <path d="M78 130 L73 142 L77 142 L71 154 L82 138 L77 138 Z" fill="#c8a84b" opacity="0.5"/>

      <defs>
        <radialGradient id="ringGrad" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#c8a84b"/>
          <stop offset="100%" stopColor="#c8a84b" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="ballGrad" x1="48" y1="48" x2="172" y2="172" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c8a84b" stopOpacity="0.8"/>
          <stop offset="100%" stopColor="#604020" stopOpacity="0.4"/>
        </linearGradient>
        <linearGradient id="topGrad" x1="48" y1="60" x2="172" y2="110" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c83030"/>
          <stop offset="100%" stopColor="#803010"/>
        </linearGradient>
        <linearGradient id="btnGrad" x1="96" y1="96" x2="124" y2="124" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0d060"/>
          <stop offset="100%" stopColor="#c8853a"/>
        </linearGradient>
        <radialGradient id="innerBtn" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#f0e8d0"/>
          <stop offset="100%" stopColor="#a08060"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

/* ─── Rule items data ───────────────────────────────────────────────────────── */
const CORE_RULES = [
  { title: 'First Encounter', text: 'You may catch only the first wild Pokémon of your chosen type encountered in each area.' },
  { title: 'Faint = Release', text: 'If a Pokémon faints, it is considered dead and must be permanently boxed or released.' },
  { title: 'Nickname Everything', text: 'Nickname every Pokémon to strengthen your bond and make losses more meaningful.' },
  { title: 'No Items in Battle', text: 'Potions, Berries, and other  items may not be used during battles. Exception for Pokeballs and held items' },
  { title: 'No Duplicates', text: 'Once you catch a species, you cannot catch another of the same species in that run.' },
  { title: 'No Over-Leveling', text: "Your Pokémon may not exceed the level of the next Gym Leader's (or Elite Four member's) highest-level Pokémon." },
];

const FEARLESS_RULES = [
  { title: 'Monotype Lock', text: 'At the start of each run, choose one Pokémon type. Only Pokémon of that type may be caught or used.' },
  { title: 'All-Type Completion', text: 'To beat the challenge, complete a Monotype run for every type in each generation you play.' },
  { title: 'No Repeat Usage', text: 'Any Pokémon used in one run is permanently ineligible for future runs — including its entire evolution line.' },
  { title: 'Generation Pool Limit', text: "You may only use Pokémon available in that game's Pokédex (e.g. 151 species in Gen I)." },
  { title: 'Sequential Runs', text: 'Start and finish each type run (to Champion) before moving on to the next type.' },
  { title: 'Exceptions', text: 'In cases of limited typing (e.g. Gen I–II), you may reuse a Pokémon if no alternative exists. Document exceptions clearly.' },
];

/* ─── Component ─────────────────────────────────────────────────────────────── */
export default function Rules() {

  return (
    <>
      {/* keyframe injector */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatBob {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-12px); }
        }
        @keyframes scrollBounce {
          0%, 100% { transform: rotate(45deg) translateY(0); }
          50%       { transform: rotate(45deg) translateY(5px); }
        }
        .rule-item:hover {
          background: rgba(200,168,75,0.06) !important;
          border-color: rgba(200,168,75,0.35) !important;
        }
        .rules-section:nth-child(1) { animation-delay: 0.1s; }
        .rules-section:nth-child(2) { animation-delay: 0.3s; }
      `}</style>

      <div style={S.page}>

        {/* ── HERO ── */}
        <section style={S.hero}>
          <div style={S.heroGrain} />

          <div style={S.heroEyecatch}>
            <HeroGraphic />
          </div>

          <p style={S.heroTagline}>The Ultimate Pokémon Challenge</p>

          <h1 style={S.heroTitle}>
            Fearless<br />Nuzlocke
          </h1>

          <p style={S.heroSub}>
            Inspired by the fan-favourite <em>Nuzlocke</em> rule set,
            fused with League of Legends' <em>Fearless Draft</em> system.
            Every run. Every type. No repeats. No mercy.
          </p>

          <div style={S.heroDivider} />

          <div style={S.scrollHint}>
            <span>Read the rules</span>
            <div style={S.scrollArrow} />
          </div>
        </section>

        {/* ── RULES ── */}
        <div style={S.rulesPage}>

          {/* Core Nuzlocke */}
          <div style={S.sectionWrap} className="rules-section">
            <p style={S.sectionLabel}>Part I</p>
            <h2 style={S.sectionTitle}>Core Nuzlocke Rules</h2>
            <p style={S.sectionIntro}>
              The foundation. These rules govern every run, no exceptions.
              Break them and the run is void.
            </p>
            <ol style={S.rulesList}>
              {CORE_RULES.map((r, i) => (
                <li key={i} style={S.ruleItem} className="rule-item">
                  <span style={S.ruleNum}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={S.ruleBody}>
                    <strong style={S.ruleTitle}>{r.title}</strong>
                    <span style={S.ruleText}>{r.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Fearless Draft */}
          <div style={S.sectionWrap} className="rules-section">
            <p style={S.sectionLabel}>Part II</p>
            <h2 style={S.sectionTitle}>Fearless Draft Additions</h2>
            <p style={S.sectionIntro}>
              Fearlesslocke rules
               <br/>
               These are rules added on to the previously mentioned Nuzlocke rules.
               <br/>
               The goal: complete a Monotype run for each of the 18 types using the full National Pokédex. 
               <br/>
               One shot. One legacy.
            </p>
            <ol style={S.rulesList}>
              {FEARLESS_RULES.map((r, i) => (
                <li key={i} style={S.ruleItem} className="rule-item">
                  <span style={S.ruleNum}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={S.ruleBody}>
                    <strong style={S.ruleTitle}>{r.title}</strong>
                    <span style={S.ruleText}>{r.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

        </div>

        <div style={S.footerStrip}>
          FEARLESS NUZLOCKE · CHALLENGE RULESET · GOOD LUCK, TRAINER
        </div>

      </div>
    </>
  );
}